import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemText,
} from "@mui/material";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";

import { ReactComponent as Exercise } from "../../assets/topbar/exercise.svg";
import { ReactComponent as Care } from "../../assets/topbar/care.svg";
import { ReactComponent as User } from "../../assets/topbar/user.svg";
import ShareIcon from "@mui/icons-material/Share";
import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined";
import NotListedLocationIcon from "@mui/icons-material/NotListedLocation";

import { ModeChangebar } from "./ModeChangebar";
import { ScrollableToolbar } from "./ScrollableToolbar";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";

export const Topbar = () => {
  const { user, updateTutorialStatus } = useAuthContext();
  const isUser = user?.securityType === 3;
  const location = useLocation();
  const navigate = useNavigate();
  const currentSection = location.pathname.split("/")[1];
  const currentPath = location.pathname;
  const [showTopbar, setShowTopbar] = useState(true);
  const [previewModalOpen, setPreviewModalOpen] = useState(false); // State for preview modal
  const [currentTutorial, setCurrentTutorial] = useState("");
  const [newModalTitle, setNewModalTitle] = useState("");
  const listItems = [
    { area: "Getting Started" },
    { area: "Exercise Plan Feature" },
    { area: "Care Plan Feature" },
    { area: "Share Feature" },
    { area: "Good Faith Estimation Feature" },
  ];
  const [newModalOpen, setNewModalOpen] = useState(false);

  useEffect(() => {
    if (user?.licenseAcceptance === 0) {
      setShowTopbar(false);
    }
  }, [user]);

  const handleExerciseClick = () => {
    navigate("/exercise");
  };

  const handleCarePlanClick = () => {
    navigate("/care-plan");
  };

  const handleShareClick = () => {
    navigate("/share");
  };

  const handleAutomationClick = () => {
    navigate("/automation");
  };

  const handleUsersClick = () => {
    navigate("/users");
  };

  const handleTutorialClick = () => {
    setPreviewModalOpen(true); // Open the preview modal
  };


  const closePreviewModal = () => {
    setPreviewModalOpen(false); // Close the modal
  };

  //need to handle the url changing
  useEffect(() => {
    const currentUrl = currentPath.split("/")[1];
    if (
      currentUrl === "exercise" ||
      currentUrl === "care-plan" ||
      currentUrl === "share"
    ) {
      if (currentPath === "/care-plan/estimation") {
        setCurrentTutorial(
          `https://d2nut6gc5qzbiv.cloudfront.net/37504600/TutorialVideos/gfe.mp4`
        );
      } else
        setCurrentTutorial(
          `https://d2nut6gc5qzbiv.cloudfront.net/37504600/TutorialVideos/${currentUrl}.mp4`
        );
    } else {
      setCurrentTutorial("");
    }
  }, [currentPath]);

  const handleItemClick = async (selectedId) => {
    setNewModalOpen(true);
    switch (selectedId) {
      case "Getting Started":
        setCurrentTutorial(
          "https://d2nut6gc5qzbiv.cloudfront.net/37504600/TutorialVideos/getting-started.mp4"
        );
        // Mark tutorial as watched if user status is pending
        if (user?.tutorialStatus === "pending") {
          try {
            await updateTutorialStatus("watched");
          } catch (error) {
            console.error("Error updating tutorial status:", error);
          }
        }
        break;
      case "Exercise Plan Feature":
        setCurrentTutorial(
          "https://d2nut6gc5qzbiv.cloudfront.net/37504600/TutorialVideos/exercise.mp4"
        );
        break;
      case "Care Plan Feature":
        setCurrentTutorial(
          "https://d2nut6gc5qzbiv.cloudfront.net/37504600/TutorialVideos/care-plan.mp4"
        );
        break;
      case "Share Feature":
        setCurrentTutorial(
          "https://d2nut6gc5qzbiv.cloudfront.net/37504600/TutorialVideos/share.mp4"
        );
        break;
      case "Good Faith Estimation Feature":
        setCurrentTutorial(
          "https://d2nut6gc5qzbiv.cloudfront.net/37504600/TutorialVideos/gfe.mp4"
        );
        break;
      default:
        setCurrentTutorial("");
    }
    setNewModalTitle(selectedId); // Set the title for the modal
  };

  const closeNewModal = () => {
    setNewModalOpen(false); // Close the modal
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          boxShadow: 0,
          py: { xs: 2, md: 4 },
          px: { xs: 1, md: 2 },
          display: showTopbar ? "block" : "none",
        }}
      >
        <Toolbar>
          <Box flexGrow={1} display="flex">
            <ScrollableToolbar>
              <Button
                onClick={handleExerciseClick}
                variant="contained"
                startIcon={<Exercise />}
                sx={{
                  fontSize: "17px",
                  px: 3,
                  py: "14px",
                  mr: 2,
                  borderRadius: "100px",
                }}
              >
                Exercises
              </Button>
              <Button
                onClick={handleCarePlanClick}
                variant="contained"
                startIcon={<Care />}
                sx={{
                  fontSize: "17px",
                  px: 3,
                  py: "14px",
                  mr: 2,
                  borderRadius: "100px",
                }}
              >
                Care Plan
              </Button>
              <Button
                onClick={handleShareClick}
                variant="contained"
                startIcon={<ShareIcon style={{ fontSize: 30 }} />}
                sx={{
                  fontSize: "17px",
                  px: 3,
                  py: "14px",
                  mr: 2,
                  borderRadius: "100px",
                }}
              >
                Share
              </Button>
              <Button
                onClick={handleAutomationClick}
                variant="contained"
                startIcon={
                  <ChangeCircleOutlinedIcon style={{ fontSize: 30 }} />
                }
                sx={{
                  fontSize: "17px",
                  px: 3,
                  py: "14px",
                  mr: 2,
                  borderRadius: "100px",
                }}
              >
                Playlist
              </Button>
              <Button
                onClick={handleTutorialClick}
                variant="contained"
                startIcon={<NotListedLocationIcon style={{ fontSize: 30 }} />}
                sx={{
                  fontSize: "17px",
                  px: 3,
                  py: "14px",
                  mr: 2,
                  borderRadius: "100px",
                }}
              >
                Tutorials
              </Button>
              {!isUser && (
                <Button
                  onClick={handleUsersClick}
                  variant="contained"
                  startIcon={<User />}
                  sx={{
                    fontSize: "17px",
                    px: 3,
                    py: "14px",
                    mr: 2,
                    borderRadius: "100px",
                  }}
                >
                  Users
                </Button>
              )}

            </ScrollableToolbar>
          </Box>
          <ModeChangebar
            visibleViewMode={
              currentSection === "videos" || currentSection === ""
                ? false
                : true
            }
            isDisable={
              currentSection === "exercise" ||
              currentSection === "care-plan" ||
              currentSection === "users" ||
              currentSection === "automation" ||
              currentSection === "share" ||
              currentSection === "estimation" ||
              currentSection === "share" ||
              currentSection === "tutorial"||
              currentSection === "refer"
                ? true
                : false
            }
          />
        </Toolbar>
      </AppBar>
      <Dialog
        open={previewModalOpen}
        onClose={closePreviewModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Tutorial</DialogTitle>
        <DialogContent>
          {currentSection !== "exercise" &&
          currentSection !== "care-plan" &&
          currentSection !== "share" ? (
            <>
              {listItems.map((item, index) => (
                <ListItem
                  key={index}
                  onClick={() => handleItemClick(item.area)}
                  sx={{
                    fontSize: { xs: "20px", lg: "22px" },
                    px: { xs: "20px", lg: "28px" },
                    py: { xs: "10px", lg: "14px" },
                    mb: 1,
                  }}
                >
                  <ListItemText primary={item.area} />
                  <PlayCircleIcon fontSize="small" />
                </ListItem>
              ))}
            </>
          ) : (
            <video width="100%" height="315" onContextMenu={(e) => e.preventDefault()} controlsList="nodownload" controls src={currentTutorial} />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closePreviewModal}
            variant="contained"
            sx={{
              borderRadius: "5px",
              fontFamily: "Poppins",
              fontSize: { xs: "16px", sm: "18px" },
              fontWeight: 500,
              textAlign: "center",
              px: 4,
              mr: 2,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={newModalOpen}
        onClose={closeNewModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{newModalTitle}</DialogTitle>
        <DialogContent>
          <video width="100%" height="315" onContextMenu={(e) => e.preventDefault()} controlsList="nodownload" controls src={currentTutorial} />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeNewModal}
            variant="contained"
            sx={{
              borderRadius: "5px",
              fontFamily: "Poppins",
              fontSize: { xs: "16px", sm: "18px" },
              fontWeight: 500,
              textAlign: "center",
              px: 4,
              mr: 2,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
